/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { logEvent } from 'src/services';

import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { CustomButton, TextButton } from 'src/components';
import { ReferralJobOpeningModal } from './ReferralJobOpeningModal';
import { ReferFriendConsentContent } from './ReferFriendConsentContent';
import { ConsentTermsDialog } from 'src/components/ConsentTermsDialog/ConsentTermsDialog';

import {
  fetchReferrerCountryOptions,
  referFriendSelectors,
  submitReferral,
} from 'src/redux/referFriend';
import { referralJobOpeningsSelectors } from 'src/redux/referralJobOpenings';
import { userGeoLocationSelector } from 'src/redux/userGeoLocation';

import { ICountry } from 'src/types';
import { decodeJoinJobData, isStatusIdle, parseQueryParams } from 'src/utils';
import {
  APP_ROUTES,
  NotAvailableReferrerCountryCodes,
  ReferrerType,
} from 'src/constants';

import { validationSchema } from '../validationSchema';

const ReferFriendForm = ({
  onReferralSubmit,
}: {
  onReferralSubmit: () => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const { data: referrerProfile } = useAppSelector(
    referFriendSelectors.getReferrerAPIData,
  );
  const {
    data: referrerCountryOptions,
    apiStatus: referrerCountryOptionsApiStatus,
  } = useAppSelector(referFriendSelectors.getReferrerCountryOptions);
  const isSubmitting = useAppSelector(
    referFriendSelectors.getReferrerIsPerformingAction,
  );
  const jobOpenings = useAppSelector(
    referralJobOpeningsSelectors.getReferralJobOpenings,
  );
  const userGeoLocation = useAppSelector(
    userGeoLocationSelector.getUserGeoLocation,
  );

  const [isAddNote, setIsAddNote] = useState(false);
  const [isOpenJobOpeningModal, setOpenJobOpeningModal] =
    useState<boolean>(false);
  const [isOpenConsentModal, setIsOpenConsentModal] = useState(false);

  const isExternalReferrer = referrerProfile?.type === ReferrerType.EXTERNAL;
  const countries = referrerCountryOptions.map((e) => e.name);

  const { utm_source, utm_medium, referred_position, utm_campaign, data } =
    parseQueryParams<{
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      referred_position?: string;
      data?: string;
    }>(location.search);

  // Job-scoped referral link from Join (same encoded payload shape as the apply link)
  const joinJobData = useMemo(
    () => (data ? decodeJoinJobData(data) : null),
    [data],
  );

  const formik = useFormik<{
    email: string | null;
    name: string | null;
    linkedIn: string | null;
    position: string | null;
    note: string | null;
    referrerName: string | null;
    referrerCountry: string | null;
    isConsentChecked: boolean;
    referrerType: string | null;
  }>({
    initialValues: {
      email: null,
      name: null,
      linkedIn: null,
      position: null,
      note: null,
      referrerName: null,
      referrerCountry: null,
      isConsentChecked: false,
      referrerType: referrerProfile?.type || null,
    },
    validationSchema,
    onSubmit: async (values) => {
      logEvent('launchpod - refer-fried-page_submit-click');

      const response = await dispatch(
        submitReferral({
          name: values.name!,
          email: values.email!,
          linkedIn: values.linkedIn || null,
          note: values.note || null,
          position: values.position || null,
          title: joinJobData?.title || null,
          specialization: joinJobData?.specialization || null,
          skill: joinJobData?.skill || null,
          jobPostingUrl: joinJobData?.jobPostingUrl || null,
          referrerName: values.referrerName || null,
          referrerCountry: values.referrerCountry || null,
          utmSource: utm_source || null,
          utmMedium: utm_medium || null,
          utmCampaign: utm_campaign || null,
        }),
      );

      if (submitReferral.fulfilled.match(response)) {
        onReferralSubmit();
      }
    },
    validateOnMount: true,
  });

  // Fetch referrer country options for external referrers to set default country based on user geolocation
  useEffect(() => {
    if (isStatusIdle(referrerCountryOptionsApiStatus) && isExternalReferrer) {
      dispatch(fetchReferrerCountryOptions());
    }
  }, [isExternalReferrer, referrerCountryOptionsApiStatus]);

  // Pre-fill the position dropdown if the referral link contains a job posting URL —
  // either the legacy `referred_position` param or a job-scoped Join link's `data` param.
  // This is a UX nicety only; for the `data` param the backend also resolves the position
  // from title/specialization/skill/jobPostingUrl regardless of whether this match succeeds.
  const referredJobPostingUrl = referred_position || joinJobData?.jobPostingUrl;

  useEffect(() => {
    if (referredJobPostingUrl && jobOpenings?.length) {
      const jobOpening = jobOpenings.find(
        (jobOpening) => jobOpening.Careers_Site_Link === referredJobPostingUrl,
      );
      formik.setFieldValue('position', jobOpening?.id ?? '');
    }
  }, [referredJobPostingUrl, jobOpenings]);

  // Update referrer country based on user geolocation if referrer is external and country is not set yet
  useEffect(() => {
    if (
      !referrerProfile?.country &&
      !formik.values.referrerCountry &&
      isExternalReferrer
    ) {
      const detectedCountryOption = referrerCountryOptions?.find(
        (country: ICountry) =>
          country.code.toLowerCase() ===
          userGeoLocation?.country_code?.toLowerCase(),
      );

      if (detectedCountryOption) {
        formik.setFieldValue('referrerCountry', detectedCountryOption.name);
      }
    }
  }, [userGeoLocation, referrerCountryOptions, isExternalReferrer]);

  useEffect(() => {
    if (referrerProfile) {
      formik.setValues({
        ...formik.values,
        referrerName: referrerProfile.name || null,
        referrerCountry: referrerProfile.country || null,
        referrerType: referrerProfile.type,
      });
    }
  }, [referrerProfile]);

  const handleCloseJobOpeningModal = useCallback(
    () => setOpenJobOpeningModal(false),
    [],
  );

  const handleMouseDownPosition = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setOpenJobOpeningModal(true);
    },
    [setOpenJobOpeningModal],
  );

  const handleSelectJobOpening = useCallback(
    (payload: string) => {
      formik.setFieldValue('position', payload);
      handleCloseJobOpeningModal();
    },
    [handleCloseJobOpeningModal],
  );

  const handleFriendNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value, name } = event.target;
    const excludedKeys = /[,|*+0-9]/;

    if (!excludedKeys.test(value)) {
      formik.setFieldValue(name, value);
    }
  };

  const onSubscribePositionsClick = useCallback(() => {
    navigate(
      isExternalReferrer
        ? `/${APP_ROUTES.POSITION_SUBSCRIPTION}`
        : `/${APP_ROUTES.REFER_FRIEND_POSITION_SUBSCRIPTION}`,
    );
  }, [isExternalReferrer]);

  return (
    <>
      <ReferralJobOpeningModal
        isOpen={isOpenJobOpeningModal}
        handleClose={handleCloseJobOpeningModal}
        handleSelectJobOpening={handleSelectJobOpening}
      />
      <Box
        display="flex"
        alignItems="center"
        flexDirection="column"
        sx={{ width: '100%' }}
      >
        <Box
          sx={(theme) => ({
            background: theme.palette.common.white,
            padding: '62px 100px',
            borderRadius: '48px',
            maxWidth: '603px',
            width: '100%',
            [theme.breakpoints.down('md')]: {
              padding: '48px 32px',
            },
          })}
        >
          <Box mb={2}>
            <Typography
              variant="h2"
              color="primary"
              sx={{ fontSize: '30px', fontWeight: 600, mb: 0 }}
            >
              Refer a friend
            </Typography>
            <Typography variant="body2" color="secondary">
              We'll send your friend a link to provide details and resume.
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              name="email"
              type="email"
              label="Your friend’s email"
              id="email"
              variant="outlined"
              autoComplete="email"
              autoFocus
              required
              fullWidth
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email || ''}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <TextField
              name="name"
              type="text"
              label="Friend's full name"
              id="name"
              variant="outlined"
              required
              fullWidth
              onChange={handleFriendNameChange}
              onBlur={formik.handleBlur}
              value={formik.values.name || ''}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            {isExternalReferrer && (
              <TextField
                name="linkedIn"
                type="text"
                label="Friend's LinkedIn profile"
                id="linkedIn"
                variant="outlined"
                required
                fullWidth
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.linkedIn || ''}
                error={
                  formik.touched.linkedIn && Boolean(formik.errors.linkedIn)
                }
                helperText={formik.touched.linkedIn && formik.errors.linkedIn}
              />
            )}
            <TextField
              select
              name="position"
              label="Position"
              id="position"
              variant="outlined"
              fullWidth
              focused={false}
              onBlur={formik.handleBlur}
              value={formik.values.position || ''}
              error={formik.touched.position && Boolean(formik.errors.position)}
              helperText={formik.touched.position && formik.errors.position}
              InputProps={{
                sx: {
                  cursor: 'pointer',
                },
              }}
              SelectProps={{
                onClick: handleMouseDownPosition,
                open: false,
              }}
            >
              <MenuItem value="">None</MenuItem>
              {jobOpenings?.map((jobOpening) => (
                <MenuItem key={jobOpening.id} value={jobOpening.id}>
                  {jobOpening.Marketing_Title}
                </MenuItem>
              ))}
            </TextField>
            {!isExternalReferrer &&
              (isAddNote ? (
                <TextField
                  name="note"
                  label="Note"
                  type="textarea"
                  id="note"
                  variant="outlined"
                  value={formik.values.note || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  maxRows={10}
                  inputProps={{ maxLength: 250 }}
                  error={!!formik.values.note && Boolean(formik.errors.note)}
                  helperText={formik.values.note && formik.errors.note}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      padding: '40px 16px 32px',
                    },
                    '& .MuiInputBase-input': {
                      padding: '0 !important',
                    },
                  }}
                />
              ) : (
                <TextButton
                  onClick={() => setIsAddNote(true)}
                  sx={{ justifyContent: 'start' }}
                >
                  Add a note
                </TextButton>
              ))}
            {isExternalReferrer && (
              <>
                <Box mt={3}>
                  <Typography fontWeight={600} fontSize="16px">
                    How to contact you
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    We'll keep you in the loop of how the hiring is going.
                  </Typography>
                </Box>
                <TextField
                  label="Your email"
                  variant="outlined"
                  fullWidth
                  value={referrerProfile?.email || ''}
                  disabled
                />
                <TextField
                  name="referrerName"
                  type="text"
                  label="Your full name"
                  id="referrerName"
                  variant="outlined"
                  required
                  fullWidth
                  onChange={(e) =>
                    formik.setFieldValue('referrerName', e.target.value)
                  }
                  onBlur={formik.handleBlur}
                  value={formik.values.referrerName || ''}
                  error={
                    formik.touched.referrerName &&
                    Boolean(formik.errors.referrerName)
                  }
                  helperText={
                    formik.touched.referrerName && formik.errors.referrerName
                  }
                />
                <Autocomplete
                  fullWidth
                  value={formik.values.referrerCountry || ''}
                  options={countries}
                  onChange={(_, newValue) =>
                    formik.setFieldValue('referrerCountry', newValue)
                  }
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="referrerCountry"
                      label="Your country"
                      required
                      error={
                        formik.touched.referrerCountry &&
                        Boolean(formik.errors.referrerCountry)
                      }
                      helperText={
                        formik.touched.referrerCountry &&
                        formik.errors.referrerCountry
                      }
                      onBlur={formik.handleBlur}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            <Stack
                              direction="row"
                              alignItems="center"
                              mt="-6px"
                            >
                              {NotAvailableReferrerCountryCodes.includes(
                                formik.values.referrerCountry!,
                              ) && <Chip label="Not supported" />}
                              {userGeoLocation?.country_name &&
                                formik.values.referrerCountry?.toLowerCase() ===
                                  userGeoLocation?.country_name?.toLowerCase() && (
                                  <Tooltip
                                    title="Based on your location"
                                    placement="top"
                                  >
                                    <MyLocationIcon
                                      fontSize="small"
                                      sx={(theme) => ({
                                        marginLeft: '0.5rem',
                                        color: theme.palette.text.secondary,
                                      })}
                                    />
                                  </Tooltip>
                                )}
                            </Stack>
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          paddingTop: '16px',
                          paddingBottom: '10px',
                        },
                      }}
                    />
                  )}
                />
                {NotAvailableReferrerCountryCodes.includes(
                  formik.values.referrerCountry!,
                ) && (
                  <Box
                    sx={(theme) => ({
                      borderRadius: '0 0 8px 8px',
                      background: theme.palette.highlight.accent,
                      padding: '12px 16px 16px',
                      marginTop: '-18px',
                    })}
                  >
                    <Typography fontWeight={500} fontSize="16px">
                      Thanks for your interest!
                      <br />
                      Your{' '}
                      <Typography
                        component="span"
                        sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                        onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setIsOpenConsentModal(true);
                        }}
                      >
                        location is not currently supported
                      </Typography>{' '}
                      for the Talent Referral Program.
                    </Typography>
                  </Box>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.isConsentChecked}
                      onChange={(e) =>
                        formik.setFieldValue(
                          'isConsentChecked',
                          e.target.checked,
                        )
                      }
                      name="isConsentChecked"
                      disabled={NotAvailableReferrerCountryCodes.includes(
                        formik.values.referrerCountry!,
                      )}
                    />
                  }
                  label={
                    <Typography mr={-2}>
                      I agree to{' '}
                      <Typography
                        component="span"
                        sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                        onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setIsOpenConsentModal(true);
                        }}
                      >
                        the program’s terms and conditions
                      </Typography>
                    </Typography>
                  }
                />
              </>
            )}
          </Box>

          <Box mt={4}>
            <CustomButton
              onClick={() => {
                formik.handleSubmit();
              }}
              label="Refer a friend"
              color="primary"
              fullWidth
              isLoading={isSubmitting}
              isDisabled={!formik.isValid}
            />
          </Box>

          <Box mt={4} sx={{ textAlign: 'center' }}>
            <TextButton
              onClick={onSubscribePositionsClick}
              variant="primary"
              sx={{ textDecoration: 'underline' }}
            >
              Subscribe for positions digest
            </TextButton>
          </Box>
        </Box>

        <Box display="flex" justifyContent="center" mt={6}>
          <Typography
            variant="body2"
            color="secondary"
            textAlign="center"
            fontSize="0.675rem"
          >
            <Link href="https://agileengine.com/privacy-policy" target="_blank">
              Privacy policy
            </Link>
          </Typography>
        </Box>
      </Box>
      <ConsentTermsDialog
        isOpen={isOpenConsentModal}
        handleClose={() => setIsOpenConsentModal(false)}
        handleConfirm={() => setIsOpenConsentModal(false)}
        title="Talent referral program terms and conditions"
        showButton={true}
        dialogProps={{ maxWidth: '800px' }}
      >
        <ReferFriendConsentContent />
      </ConsentTermsDialog>
    </>
  );
};

export default ReferFriendForm;
